import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaVenusMars,
  FaCalendarAlt,
  FaInstagram,
  FaUser,
  FaSpinner,
  FaCheck,
  FaTimes,
  FaTh,
  FaList,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import campaignApplicationService from '../../services/campaignApplicationService';
import { getCampaignById } from '../../services/companyAuthService';
import countries from '../../../data/countries.json';
import CampaignApplicantsTableView from './CampaignApplicantsTableView';
import AgreementModal from './AgreementModal';

const REJECTION_REASONS = [
  'Not a good fit',
  'Insufficient followers',
  'Content quality concerns',
  'Budget constraints',
  'Timeline mismatch',
  'Other'
];

const CampaignApplicants = () => {
  const { advertisingId } = useParams();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionData, setRejectionData] = useState({
    reason: '',
    notes: ''
  });
  const [applicationToReject, setApplicationToReject] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [applicationToApprove, setApplicationToApprove] = useState(null);
  const [approvalData, setApprovalData] = useState({
    commissionModel: 'fixed',
    amount: '',
    percentage: '',
    cpe: '',
    cpm: '',
    agreementStatus: 'agreed_with_influencer',
    currency: null
  });
  const [loadingApprovalMeta, setLoadingApprovalMeta] = useState(false);

  const fetchApplications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await campaignApplicationService.getApplications(advertisingId);

      if (response.success) {
        setApplications(response.data || []);
      } else {
        setError(response.message || 'Failed to fetch applications');
      }
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError(err.message || 'Failed to fetch applications');
      toast.error(err.message || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, [advertisingId])

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);


  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'N/A';
    return phone;
  };

  const getCountryName = (countryCode) => {
    if (!countryCode) return 'Not specified';
    const country = countries.find(c => c.code === countryCode || c.name === countryCode);
    return country ? country.name : countryCode;
  };

  const getInitial = (name) => {
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
  };

  const normalizeProposedRate = (rate) => {
    if (rate === undefined || rate === null) return null;

    if (typeof rate === 'number') {
      return {
        amount: rate,
        code: 'INR',
        symbol: '₹'
      };
    }

    if (typeof rate === 'object') {
      const amount = Number(rate.amount ?? rate.value ?? 0);
      const normalizedAmount = Number.isFinite(amount) ? Math.max(0, amount) : 0;
      const currency = rate.currency || {};

      return {
        amount: normalizedAmount,
        code: (currency.code || rate.code || 'INR').toUpperCase(),
        symbol: currency.symbol || rate.symbol || '₹'
      };
    }

    const fallbackAmount = Number(rate);
    return Number.isFinite(fallbackAmount)
      ? { amount: Math.max(0, fallbackAmount), code: 'INR', symbol: '₹' }
      : null;
  };

  const handleStatusUpdate = async (applicationId, newStatus, rejectionReason = null, rejectionNotes = null) => {
    try {
      setUpdatingStatus(prev => ({ ...prev, [applicationId]: true }));

      const updateData = { status: newStatus };
      if (rejectionReason) {
        updateData.rejectionReason = rejectionReason;
      }
      if (rejectionNotes) {
        updateData.rejectionNotes = rejectionNotes;
      }

      await campaignApplicationService.updateApplicationStatus(applicationId, updateData);

      // Update local state
      setApplications(prev => prev.map(app =>
        app._id === applicationId
          ? { ...app, status: newStatus, rejectionReason, rejectionNotes }
          : app
      ));

      // Update selected application if it's the one being updated
      if (selectedApplication && selectedApplication._id === applicationId) {
        setSelectedApplication(prev => ({
          ...prev,
          status: newStatus,
          rejectionReason,
          rejectionNotes
        }));
      }

      toast.success(`Application ${newStatus} successfully`);

      // Close rejection modal if open
      if (showRejectionModal) {
        setShowRejectionModal(false);
        setRejectionData({ reason: '', notes: '' });
        setApplicationToReject(null);
      }
    } catch (err) {
      console.error('Error updating application status:', err);
      toast.error(err.message || 'Failed to update application status');
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [applicationId]: false }));
    }
  };

  const handleRejectClick = (application) => {
    setApplicationToReject(application);
    setShowRejectionModal(true);
  };

  const handleRejectionSubmit = () => {
    if (!rejectionData.reason) {
      toast.error('Please select a rejection reason');
      return;
    }

    if (applicationToReject) {
      handleStatusUpdate(
        applicationToReject._id,
        'rejected',
        rejectionData.reason,
        rejectionData.notes
      );
    }
  };

  const handleAcceptClick = async (application) => {
    try {
      setApplicationToApprove(application);
      setShowApprovalModal(true);
      setLoadingApprovalMeta(true);

      const campaignId = application.campaignId?._id || application.campaignId;
      if (!campaignId) {
        setLoadingApprovalMeta(false);
        toast.error('Unable to load campaign details for this application');
        return;
      }

      const response = await getCampaignById(campaignId);
      const campaign = response.campaign || response.data?.campaign || response;

      if (campaign && campaign.commission && campaign.commission.currency) {
        setApprovalData(prev => ({
          ...prev,
          commissionModel: campaign.commission.model || 'fixed',
          amount: campaign.commission.amount ?? '',
          percentage: campaign.commission.percentage ?? '',
          cpe: campaign.commission.cpe ?? '',
          cpm: campaign.commission.cpm ?? '',
          currency: campaign.commission.currency
        }));
      } else {
        toast.warn('Campaign commission details not found. Please fill the agreement manually.');
        setApprovalData(prev => ({
          ...prev,
          commissionModel: 'fixed',
          amount: '',
          percentage: '',
          cpe: '',
          cpm: '',
          currency: null
        }));
      }
    } catch (err) {
      console.error('Error loading campaign details for approval:', err);
      toast.error(err.message || 'Failed to load campaign details');
    } finally {
      setLoadingApprovalMeta(false);
    }
  };

  const handleApprovalSubmit = async () => {
    if (!applicationToApprove) return;

    const { commissionModel, amount, percentage, cpe, cpm, agreementStatus } = approvalData;

    if (!commissionModel) {
      toast.error('Please select a commission model');
      return;
    }

    // Allow 0 as a valid value - check for empty string/null first, then validate number (including 0)
    if (commissionModel === 'fixed') {
      if (amount === '' || amount === null || amount === undefined) {
        toast.error('Please enter a fixed amount');
        return;
      }
      const numAmount = Number(amount);
      if (isNaN(numAmount) || numAmount < 0) {
        toast.error('Please enter a valid fixed amount (0 or greater)');
        return;
      }
    }

    if (commissionModel === 'percentage') {
      if (percentage === '' || percentage === null || percentage === undefined) {
        toast.error('Please enter a percentage value');
        return;
      }
      const numPercentage = Number(percentage);
      if (isNaN(numPercentage) || numPercentage < 0 || numPercentage > 100) {
        toast.error('Please enter a valid percentage value (0-100)');
        return;
      }
    }

    // CPE validation - allow 0
    if (cpe === '' || cpe === null || cpe === undefined) {
      toast.error('Please enter a CPE value');
      return;
    }
    const numCpe = Number(cpe);
    if (isNaN(numCpe) || numCpe < 0) {
      toast.error('Please enter a valid CPE value (0 or greater)');
      return;
    }

    // CPM validation - allow 0
    if (cpm === '' || cpm === null || cpm === undefined) {
      toast.error('Please enter a CPM value');
      return;
    }
    const numCpm = Number(cpm);
    if (isNaN(numCpm) || numCpm < 0) {
      toast.error('Please enter a valid CPM value (0 or greater)');
      return;
    }

    if (!agreementStatus) {
      toast.error('Please select an agreement status');
      return;
    }

    const applicationId = applicationToApprove._id;

    try {
      setUpdatingStatus(prev => ({ ...prev, [applicationId]: true }));

      const payload = {
        status: 'accepted',
        commissionModel,
        amount: commissionModel === 'fixed' ? Number(amount) : undefined,
        percentage: commissionModel === 'percentage' ? Number(percentage) : undefined,
        cpe: Number(cpe),
        cpm: Number(cpm),
        agreementStatus
      };

      await campaignApplicationService.updateApplicationStatus(applicationId, payload);

      setApplications(prev => prev.map(app =>
        app._id === applicationId
          ? { ...app, status: 'accepted' }
          : app
      ));

      if (selectedApplication && selectedApplication._id === applicationId) {
        setSelectedApplication(prev => ({
          ...prev,
          status: 'accepted'
        }));
      }

      toast.success('Application accepted successfully');
      setShowApprovalModal(false);
      setApplicationToApprove(null);
      setApprovalData({
        commissionModel: 'fixed',
        amount: '',
        percentage: '',
        cpe: '',
        cpm: '',
        agreementStatus: 'agreed_with_influencer',
        currency: approvalData.currency
      });
    } catch (err) {
      console.error('Error accepting application with agreement:', err);
      toast.error(err.message || 'Failed to accept application');
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [applicationId]: false }));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted':
      case 'approved':
        return 'bg-green-100 text-green-700';
      case 'shortlisted':
        return 'bg-blue-100 text-blue-700';
      case 'reviewed':
        return 'bg-yellow-100 text-yellow-700';
      case 'rejected':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const canShowActions = (status) => {
    return status === 'pending' || status === 'reviewed';
  };

  if (loading) {
    return (
      <div className="min-h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-orange-500 text-4xl mx-auto mb-4" />
          <p className="lg:text-gray-600 text-gray-600">Loading applicants...</p>
        </div>
      </div>
    );
  }

  if (error && applications.length === 0) {
    return (
      <div className="min-h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900 mb-4 transition-colors"
          >
            <FaArrowLeft />
            <span className="font-medium">Back</span>
          </button>
          <div className="flex items-center justify-between">
            <div>
              <p className="lg:text-xs text-xs font-semibold uppercase text-orange-500">
                Campaign Applicants
              </p>
              <h1 className="lg:text-2xl text-xl font-semibold text-gray-900">
                All Applicants ({applications.length})
              </h1>
            </div>
            {/* View Toggle Buttons */}
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${viewMode === 'grid'
                  ? 'bg-white text-orange-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
                title="Grid View"
              >
                <FaTh />
                <span className="hidden sm:inline">Grid</span>
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${viewMode === 'table'
                  ? 'bg-white text-orange-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
                title="Table View"
              >
                <FaList />
                <span className="hidden sm:inline">Table</span>
              </button>
            </div>
          </div>
        </div>

        {/* Applications View */}
        {applications.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <FaUser className="text-gray-400 text-6xl mx-auto mb-4" />
            <p className="lg:text-gray-600 text-gray-600 text-lg">No applicants yet</p>
            <p className="lg:text-gray-500 text-gray-500 text-sm mt-2">
              Applications will appear here when influencers apply for this campaign.
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {applications.map((application) => (
              <div
                key={application._id}
                className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
              >
                {/* Profile Header */}
                {/* <div className="relative lg:h-32 md:h-24 h-16 bg-gradient-to-r from-orange-400 to-orange-600">
                  <div className="absolute -bottom-12 left-4">
                    <div className="relative h-24 w-24 rounded-full border-4 border-white bg-white shadow-md flex items-center justify-center text-3xl font-bold text-orange-500 overflow-hidden">
                      {application.profileImage ? (
                        <img
                          src={application.profileImage}
                          alt={application.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        getInitial(application.name)
                      )}
                    </div>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                        application.status
                      )}`}
                    >
                      {application.status}
                    </span>
                  </div>
                </div> */}
                {/* Profile Header */}
                <div className="relative h-20 sm:h-24 md:h-28 lg:h-32 bg-gradient-to-r from-orange-400 to-orange-600">

                  {/* Profile Image */}
                  <div className="absolute -bottom-8 sm:-bottom-10 md:-bottom-12 left-4">
                    <div
                      className="relative h-16 w-16 sm:h-20 sm:w-20 md:h-24 md:w-24 rounded-full border-4 border-white bg-white shadow-md flex items-center justify-center text-lg sm:text-xl md:text-3xl font-bold text-orange-500 overflow-hidden"
                    >
                      {application.profileImage ? (
                        <img
                          src={application.profileImage}
                          alt={application.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        getInitial(application.name)
                      )}
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className="absolute top-2 sm:top-3 md:top-4 right-2 sm:right-3 md:right-4">
                    <span
                      className={`px-2 py-1 
                  text-[10px] sm:text-xs 
                  rounded-full 
                  font-semibold 
                  ${getStatusColor(application.status)}`}
                    >
                      {application.status}
                    </span>
                  </div>
                </div>
                
                {/* Content */}
                <div className="pt-16 px-4 pb-4">
                  <div className="mb-2">
                    <h3 className="lg:text-lg text-base font-bold text-gray-900 truncate">
                      {application.name}
                    </h3>
                    <p className="lg:text-sm text-xs text-gray-600 truncate">
                      @{application.instagramUsername || 'N/A'}
                    </p>
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                    <div className="bg-gray-50 rounded-lg p-2">
                      <p className="lg:text-xs text-xs text-gray-500">Followers</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {application.followerCount?.toLocaleString() || '0'}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2">
                      <p className="lg:text-xs text-xs text-gray-500">Following</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {application.followingCount?.toLocaleString() || '0'}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2">
                      <p className="lg:text-xs text-xs text-gray-500">Media</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {application.mediaCount?.toLocaleString() || '0'}
                      </p>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="flex items-center gap-2 lg:text-sm text-xs text-gray-600 mb-2">
                    <FaMapMarkerAlt className="text-gray-400" />
                    <span>
                      {application.location?.city || 'N/A'}, {application.location?.country || 'N/A'}
                    </span>
                  </div>

                  {/* Categories */}
                  {application.category && application.category.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {application.category.slice(0, 3).map((cat, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-medium"
                        >
                          {cat}
                        </span>
                      ))}
                      {application.category.length > 3 && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                          +{application.category.length - 3}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Applied Date */}
                  <p className="lg:text-xs text-xs text-gray-500 mb-4">
                    Applied {formatDate(application.createdAt)}
                  </p>

                  {/* Action Buttons - Only show for pending/reviewed */}
                  {canShowActions(application.status) && (
                    <div className="flex gap-2 pt-4 border-t border-gray-200">
                      <button
                        onClick={() => handleAcceptClick(application)}
                        disabled={updatingStatus[application._id]}
                        className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Accept"
                      >
                        {updatingStatus[application._id] ? (
                          <FaSpinner className="animate-spin mr-2" />
                        ) : (
                          <FaCheck className="mr-2" />
                        )}
                        Approve
                      </button>
                      <button
                        onClick={() => handleRejectClick(application)}
                        disabled={updatingStatus[application._id]}
                        className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Reject"
                      >
                        {updatingStatus[application._id] ? (
                          <FaSpinner className="animate-spin mr-2" />
                        ) : (
                          <FaTimes className="mr-2" />
                        )}
                        Reject
                      </button>
                    </div>
                  )}

                  {/* View Details Button */}
                  <button
                    onClick={() => setSelectedApplication(application)}
                    className="w-full mt-2 px-4 py-2 text-sm font-medium text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-lg transition-colors"
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <CampaignApplicantsTableView
            applications={applications}
            updatingStatus={updatingStatus}
            onViewDetails={setSelectedApplication}
            onAccept={handleAcceptClick}
            onReject={handleRejectClick}
            canShowActions={canShowActions}
            // getStatusColor={getStatusColor}
            formatDate={formatDate}
            getCountryName={getCountryName}
          />
        )}

        {/* Rejection Modal */}
        {showRejectionModal && applicationToReject && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowRejectionModal(false);
              setRejectionData({ reason: '', notes: '' });
              setApplicationToReject(null);
            }}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="lg:text-xl text-base font-bold text-gray-900">
                  Reject Application
                </h2>
                <p className="lg:text-sm text-xs text-gray-600 mt-1">
                  Please provide a reason for rejecting {applicationToReject.name}'s application
                </p>
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <label className="block lg:text-sm text-xs font-medium text-gray-700 mb-2">
                    Rejection Reason <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={rejectionData.reason}
                    onChange={(e) => setRejectionData(prev => ({ ...prev, reason: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">Select a reason</option>
                    {REJECTION_REASONS.map((reason) => (
                      <option key={reason} value={reason}>
                        {reason}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-6">
                  <label className="block lg:text-sm text-xs font-medium text-gray-700 mb-2">
                    Additional Notes (Optional)
                  </label>
                  <textarea
                    value={rejectionData.notes}
                    onChange={(e) => setRejectionData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Provide additional details about the rejection..."
                    rows={4}
                    maxLength={500}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                  />
                  <p className="lg:text-xs text-xs text-gray-500 mt-1">
                    {rejectionData.notes.length}/500 characters
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowRejectionModal(false);
                      setRejectionData({ reason: '', notes: '' });
                      setApplicationToReject(null);
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRejectionSubmit}
                    disabled={!rejectionData.reason || updatingStatus[applicationToReject._id]}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {updatingStatus[applicationToReject._id] ? (
                      <span className="flex items-center justify-center">
                        <FaSpinner className="animate-spin mr-2" />
                        Rejecting...
                      </span>
                    ) : (
                      'Reject Application'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Application Detail Modal */}
        {selectedApplication && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedApplication(null)}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[70vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <h2 className="lg:text-xl text-base font-bold text-gray-900">
                  {selectedApplication.name}'s Application
                </h2>
                <button
                  onClick={() => setSelectedApplication(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              <div className="lg:p-6 p-4">
                {/* Profile Section */}
                <div className="flex items-start gap-6 mb-6 pb-6 border-b border-gray-200">
                  <div className="relative lg:h-32 lg:w-32 md:h-24 md:w-24 h-16 w-16 rounded-full border-4 border-orange-500 bg-white shadow-md flex items-center justify-center text-4xl font-bold text-orange-500 overflow-hidden">
                    {selectedApplication.profileImage ? (
                      <img
                        src={selectedApplication.profileImage}
                        alt={selectedApplication.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      getInitial(selectedApplication.name)
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="lg:text-2xl text-xl font-bold text-gray-900 mb-1">
                      {selectedApplication.name}
                    </h3>
                    <p className="lg:text-sm text-xs text-gray-600 mb-4">
                      @{selectedApplication.instagramUsername || 'N/A'}
                    </p>
                    <div className="flex flex-wrap gap-4 text-sm">
                      <div>
                        <span className="lg:text-sm text-xs text-gray-500">Followers: </span>
                        <span className="font-semibold">
                          {selectedApplication.followerCount?.toLocaleString() || '0'}
                        </span>
                      </div>
                      <div>
                        <span className="lg:text-sm text-xs text-gray-500">Following: </span>
                        <span className="font-semibold">
                          {selectedApplication.followingCount?.toLocaleString() || '0'}
                        </span>
                      </div>
                      <div>
                        <span className="lg:text-sm text-xs text-gray-500">Media: </span>
                        <span className="font-semibold">
                          {selectedApplication.mediaCount?.toLocaleString() || '0'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="lg:text-lg text-base font-semibold text-gray-900 mb-4">Contact Information</h4>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <FaEnvelope className="text-orange-500 mt-1" />
                        <div>
                          <p className="lg:text-xs text-xs text-gray-500">Email</p>
                          <p className="text-sm font-medium text-gray-900">
                            {selectedApplication.email || 'N/A'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <FaPhone className="text-orange-500 mt-1" />
                        <div>
                          <p className="lg:text-xs text-xs text-gray-500">Contact/WhatsApp</p>
                          <p className="text-sm font-medium text-gray-900">
                            {formatPhoneNumber(selectedApplication.contactNumber)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <FaInstagram className="text-orange-500 mt-1" />
                        <div>
                          <p className="lg:text-xs text-xs text-gray-500">Instagram</p>
                          <a
                            href={selectedApplication.instagramProfileLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium text-blue-600 hover:underline"
                          >
                            {selectedApplication.instagramProfileLink || 'N/A'}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="lg:text-lg text-base font-semibold text-gray-900 mb-4">Location</h4>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <FaMapMarkerAlt className="text-orange-500 mt-1" />
                        <div>
                          <p className="lg:text-xs text-xs text-gray-500">City</p>
                          <p className="text-sm font-medium text-gray-900">
                            {selectedApplication.location?.city || 'N/A'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <FaMapMarkerAlt className="text-orange-500 mt-1" />
                        <div>
                          <p className="lg:text-xs text-xs text-gray-500">Pin Code</p>
                          <p className="text-sm font-medium text-gray-900">
                            {selectedApplication.location?.pinCode || 'N/A'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <FaMapMarkerAlt className="text-orange-500 mt-1" />
                        <div>
                          <p className="lg:text-xs text-xs text-gray-500">Country</p>
                          <p className="text-sm font-medium text-gray-900">
                            {getCountryName(selectedApplication.location?.country)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Personal Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="lg:text-lg text-base font-semibold text-gray-900 mb-4">Personal Information</h4>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <FaVenusMars className="text-orange-500 mt-1" />
                        <div>
                          <p className="lg:text-xs text-xs text-gray-500">Gender</p>
                          <p className="text-sm font-medium text-gray-900">
                            {selectedApplication.gender || 'N/A'}
                          </p>
                        </div>
                      </div>
                      {selectedApplication.age && (
                        <div className="flex items-start gap-3">
                          <FaCalendarAlt className="text-orange-500 mt-1" />
                          <div>
                            <p className="lg:text-xs text-xs text-gray-500">Age</p>
                            <p className="text-sm font-medium text-gray-900">
                              {selectedApplication.age} years
                            </p>
                          </div>
                        </div>
                      )}
                      {selectedApplication.height && (
                        <div className="flex items-start gap-3">
                          <FaUser className="text-orange-500 mt-1" />
                          <div>
                            <p className="lg:text-xs text-xs text-gray-500">Height</p>
                            <p className="text-sm font-medium text-gray-900">
                              {selectedApplication.height}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h4 className="lg:text-lg text-base font-semibold text-gray-900 mb-4">Content & Categories</h4>
                    <div className="space-y-3">
                      {selectedApplication.category && selectedApplication.category.length > 0 && (
                        <div>
                          <p className="lg:text-xs text-xs text-gray-500 mb-2">Categories</p>
                          <div className="flex flex-wrap gap-2">
                            {selectedApplication.category.map((cat, idx) => (
                              <span
                                key={idx}
                                className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium"
                              >
                                {cat}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {selectedApplication.language && selectedApplication.language.length > 0 && (
                        <div>
                          <p className="lg:text-xs text-xs text-gray-500 mb-2">Languages</p>
                          <div className="flex flex-wrap gap-2">
                            {selectedApplication.language.map((lang, idx) => (
                              <span
                                key={idx}
                                className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium"
                              >
                                {lang}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Insight */}
                {selectedApplication.insight && (
                  <div className="mb-6">
                    <h4 className="lg:text-lg text-base font-semibold text-gray-900 mb-2">Insight</h4>
                    <p className="lg:text-sm text-xs text-gray-700 whitespace-pre-line bg-gray-50 p-4 rounded-lg">
                      {selectedApplication.insight}
                    </p>
                  </div>
                )}

                {/* Instagram Insights */}
                <div className="mb-6">
                  <h4 className="lg:text-lg text-base font-semibold text-gray-900 mb-4">
                    Instagram Insights
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                    {/* Reels */}
                    <div className="bg-gray-50 p-4 rounded-lg border shadow-sm">
                      <h5 className="font-bold text-gray-800 mb-3">🎥 Reels (Avg)</h5>

                      {selectedApplication.instagramAverages?.reels && 
                       Object.keys(selectedApplication.instagramAverages.reels).length > 0 ? (
                        Object.entries(selectedApplication.instagramAverages.reels).map(([key, value]) => (
                          <p key={key} className="text-sm">
                            {key}: <b>{value}</b>
                          </p>
                        ))
                      ) : (
                        <p className="text-gray-500 text-sm">No reel insights submitted.</p>
                      )}
                    </div>

                    {/* Photos */}
                    <div className="bg-gray-50 p-4 rounded-lg border shadow-sm">
                      <h5 className="font-bold text-gray-800 mb-3">📸 Photos (Avg)</h5>

                      {selectedApplication.instagramAverages?.posts && 
                       Object.keys(selectedApplication.instagramAverages.posts).length > 0 ? (
                        Object.entries(selectedApplication.instagramAverages.posts).map(([key, value]) => (
                          <p key={key} className="text-sm">
                            {key}: <b>{value}</b>
                          </p>
                        ))
                      ) : (
                        <p className="text-gray-500 text-sm">No photo insights submitted.</p>
                      )}
                    </div>

                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Attachment */}
                  {selectedApplication.attachment?.url && (
                    <div className="mb-6">
                      <h4 className="lg:text-lg text-base font-semibold text-gray-900 mb-2">
                        Attachment
                      </h4>

                      {/* IMAGE PREVIEW */}
                      {selectedApplication.attachment.mimeType?.startsWith('image') ? (
                        <div
                          className="inline-block cursor-pointer"
                          onClick={() =>
                            window.open(selectedApplication.attachment.url, '_blank')
                          }
                        >
                          <img
                            src={selectedApplication.attachment.url}
                            alt="Application Attachment"
                            className="max-w-xs max-h-60 rounded-lg border shadow-sm hover:shadow-md transition"
                          />
                          <p className="mt-2 text-sm text-orange-600 font-medium">
                            Click to open image in new tab
                          </p>
                        </div>
                      ) : (
                        /* PDF PREVIEW */
                        <div className="flex items-center gap-3">
                          <div className="h-12 w-12 flex items-center justify-center bg-red-100 text-red-600 rounded-lg font-bold">
                            PDF
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {selectedApplication.attachment.originalName || 'Document'}
                            </p>
                            <button
                              onClick={() =>
                                window.open(selectedApplication.attachment.url, '_blank')
                              }
                              className="text-sm text-orange-600 hover:underline font-medium"
                            >
                              View PDF in new tab
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {(() => {
                    const proposedRate = normalizeProposedRate(selectedApplication.proposedRate);
                    if (!proposedRate) return null;

                    return (
                      <div className="mb-6">
                        <h4 className="lg:text-lg text-base font-semibold text-gray-900 mb-2">
                          Proposed Rate ({proposedRate.code})
                        </h4>
                        <p className="lg:text-sm text-xs text-gray-700 whitespace-pre-line bg-gray-50 p-4 rounded-lg">
                          {proposedRate.symbol ? `${proposedRate.symbol} ` : ''}{proposedRate.amount}
                        </p>
                      </div>
                    );
                  })()}
                </div>

                {/* Address (for barter) */}
                {selectedApplication.address && (
                  <div className="mb-6">
                    <h4 className="lg:text-lg text-base font-semibold text-gray-900 mb-2">Address (for barter)</h4>
                    <p className="lg:text-sm text-xs text-gray-700 whitespace-pre-line bg-gray-50 p-4 rounded-lg">
                      {selectedApplication.address}
                    </p>
                  </div>
                )}

                {/* Agency Name */}
                {selectedApplication.agencyName && (
                  <div className="mb-6">
                    <h4 className="lg:text-lg text-base font-semibold text-gray-900 mb-2">Agency Name</h4>
                    <p className="lg:text-sm text-xs text-gray-700 whitespace-pre-line bg-gray-50 p-4 rounded-lg">
                      {selectedApplication.agencyName}
                    </p>
                  </div>
                )}

                {/* Rejection Info */}
                {selectedApplication.status === 'rejected' && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <h4 className="lg:text-sm text-xs font-semibold text-red-900 mb-2">Rejection Details</h4>
                    {selectedApplication.rejectionReason && (
                      <p className="lg:text-sm text-xs text-red-700 mb-1">
                        <span className="font-medium">Reason:</span> {selectedApplication.rejectionReason}
                      </p>
                    )}
                    {selectedApplication.rejectionNotes && (
                      <p className="lg:text-sm text-xs text-red-700">
                        <span className="font-medium">Notes:</span> {selectedApplication.rejectionNotes}
                      </p>
                    )}
                  </div>
                )}

                {/* Application Status and Actions */}
                <div className="pt-6 border-t border-gray-200">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="lg:text-sm text-xs text-gray-500 mb-2">Application Status</p>
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(
                          selectedApplication.status
                        )}`}
                      >
                        {selectedApplication.status}
                      </span>
                    </div>
                    <div className="flex w-full flex-col gap-3 sm:w-auto sm:items-end">
                      <p className="lg:text-sm text-xs text-gray-500 sm:text-right">
                        Applied on {formatDate(selectedApplication.createdAt)}
                      </p>
                      {canShowActions(selectedApplication.status) && (
                        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                          <button
                            onClick={() => {
                              setSelectedApplication(null);
                              handleAcceptClick(selectedApplication);
                            }}
                            disabled={updatingStatus[selectedApplication._id]}
                            className="inline-flex w-full items-center justify-center px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto"
                          >
                            {updatingStatus[selectedApplication._id] ? (
                              <FaSpinner className="animate-spin mr-2" />
                            ) : (
                              <FaCheck className="mr-2" />
                            )}
                            Accept
                          </button>
                          <button
                            onClick={() => {
                              setSelectedApplication(null);
                              handleRejectClick(selectedApplication);
                            }}
                            disabled={updatingStatus[selectedApplication._id]}
                            className="inline-flex w-full items-center justify-center px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto"
                          >
                            {updatingStatus[selectedApplication._id] ? (
                              <FaSpinner className="animate-spin mr-2" />
                            ) : (
                              <FaTimes className="mr-2" />
                            )}
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Approval / Agreement Modal (rendered last so it appears on top) */}
        <AgreementModal
          isOpen={showApprovalModal}
          onClose={() => {
            setShowApprovalModal(false);
            setApplicationToApprove(null);
          }}
          application={applicationToApprove}
          approvalData={approvalData}
          setApprovalData={setApprovalData}
          loadingApprovalMeta={loadingApprovalMeta}
          updatingStatus={updatingStatus}
          onSubmit={handleApprovalSubmit}
        />
      </div>
    </div>
  );
};

export default CampaignApplicants;
